// src/modules/units/tyres/shared/tyre-import-validate.js
//
// Turns spreadsheet rows + a column mapping into import rows: a POST /api/tyre
// payload per row, the problems found, and what the importer intends to do with it.
// Pure — no DOM, no I/O; everything it needs from the server (yards, suppliers,
// the tyres that already exist) is passed in.
//
// Each row ends in exactly one ACTION:
//   create  — the ID is not on file; it will be created
//   update  — the ID is on file and the user chose "update existing"
//   skip    — the ID is on file and the user chose "skip existing"
//   error   — the row cannot be imported as it stands (listed, never sent)
// Warnings never block: the row imports and the warning says what was assumed.
//
// Existence here is a PREVIEW, taken from a sweep of the account's tyres. The
// runner (tyre-import-run.js) re-checks each ID with GET /tyre/{id} immediately
// before writing, so a tyre created by someone else between Review and Import is
// still handled correctly. The preview is for the human; the write trusts the check.
//
// Tyre IDs are compared case-insensitively and trimmed. The API is backed by SQL
// Server, whose default collation is case-insensitive, so "demo-tyr-1" and
// "DEMO-TYR-1" are the same record — treating them as two would let one row
// silently overwrite the other within a single import.

import { IMPORT_FIELDS, getImportField } from './tyre-import-schema.js';
import {
  isBlankCell, cleanText, parseNumber, parseDate, roundTo, matchVocab
} from './tyre-import-values.js';
import { hasDistanceTracking } from './tyre-metrics.js';
import {
  canonicalTyreSize, catalogueSpecFromTyre, catalogueSpecLabel, matchCatalogue, planCatalogueEntries
} from './tyre-catalogue-match.js';

export const IMPORT_ACTION = Object.freeze({
  CREATE: 'create',
  UPDATE: 'update',
  SKIP: 'skip',
  ERROR: 'error'
});

export const EXISTING_MODE = Object.freeze({ UPDATE: 'update', SKIP: 'skip' });

/** The comparison key for a tyre ID (see the note above on case). */
export const tyreIdKey = (id) => String(id ?? '').trim().toUpperCase();

// hasDistanceTracking() lives in tyre-metrics.js, shared with the tyre record form.

const norm = (s) => String(s ?? '').trim().toLowerCase();

// ---------------------------------------------------------------------------
// Yard / supplier choices — ONE labelling rule, shared with the import template
// (views/import/tyreimport-template.js), whose dropdowns write these labels into
// the spreadsheet. A label must therefore resolve back to exactly one record.
//
// A yard is labelled by its name; when two selectable yards share a name (e.g.
// "Main" under two child customers) both take "name · customer", the same form
// the yard selectors show. A supplier is labelled by its name; suppliers sharing
// a name take "name (id)" so neither is silently chosen over the other.
// ---------------------------------------------------------------------------

/** [{ label, yard }] in name order, every label unique (case-insensitively). */
export function yardChoices(yards = []) {
  const counts = new Map();
  for (const y of yards) counts.set(norm(y?.name), (counts.get(norm(y?.name)) || 0) + 1);
  const seen = new Set();
  const out = [];
  for (const y of yards) {
    if (!y || norm(y.name) === '') continue;
    let label = String(y.name).trim();
    if (counts.get(norm(y.name)) > 1) label = `${label} · ${String(y.customerName || y.Id).trim()}`;
    if (seen.has(norm(label))) label = `${label} · ${y.Id}`;
    seen.add(norm(label));
    out.push({ label, yard: y });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
}

/** [{ label, supplier }] in name order, every label unique (case-insensitively). */
export function supplierChoices(suppliers = []) {
  const counts = new Map();
  for (const s of suppliers) counts.set(norm(s?.supplierName), (counts.get(norm(s?.supplierName)) || 0) + 1);
  const out = [];
  for (const s of suppliers) {
    const k = norm(s?.supplierName);
    if (!k) continue;
    const name = String(s.supplierName).trim();
    out.push({ label: counts.get(k) > 1 ? `${name} (${s.supplierId})` : name, supplier: s });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
}

// Resolve a cell to one record: by id, then by its choice label, then by bare
// name — a bare name shared by several records is AMBIGUOUS, never a guess.
function resolveChoice(text, records, choices, idOf, nameOf) {
  const k = norm(text);
  const byId = records.find((r) => norm(idOf(r)) === k);
  if (byId) return { record: byId };
  const byLabel = choices.find((c) => norm(c.label) === k);
  if (byLabel) return { record: byLabel.record };
  const byName = records.filter((r) => norm(nameOf(r)) === k);
  if (byName.length === 1) return { record: byName[0] };
  return byName.length > 1 ? { ambiguous: byName.length } : {};
}

function findYard(text, yards) {
  const choices = yardChoices(yards).map((c) => ({ label: c.label, record: c.yard }));
  return resolveChoice(text, yards, choices, (y) => y.Id, (y) => y.name);
}

function findSupplier(text, suppliers) {
  const choices = supplierChoices(suppliers).map((c) => ({ label: c.label, record: c.supplier }));
  return resolveChoice(text, suppliers, choices, (s) => s.supplierId, (s) => s.supplierName);
}

// ---------------------------------------------------------------------------
// Suppliers are kept PER CUSTOMER: a child customer buys from its own branch
// (Palm Tyres George, Palm Tyres Robertson), and the API refuses a tyre whose
// supplierId isn't owned by the tyre's customerId ("Supplier not found"). A tyre's
// customer is its yard's customer, so a row's supplier is matched only among the
// suppliers of that customer.
// ---------------------------------------------------------------------------

const sameId = (a, b) => String(a) === String(b);
const hasValue = (v) => v !== undefined && v !== null && v !== '';

/**
 * The suppliers a tyre owned by `customerId` may use. Unknown customer → all
 * (the API then scopes to the signed-in account); a supplier record without a
 * customerId is treated as usable (older responses).
 */
export function suppliersForCustomer(suppliers = [], customerId) {
  if (!hasValue(customerId)) return suppliers;
  return suppliers.filter((s) => !hasValue(s?.customerId) || sameId(s.customerId, customerId));
}

/**
 * Coerce one cell for one field.
 * @returns {{value:*, error?:string, warning?:string}} value undefined = omit
 */
export function coerceCell(field, raw, ctx = {}) {
  if (isBlankCell(raw)) return { value: undefined };
  const label = field.label;

  switch (field.type) {
    case 'number':
    case 'integer': {
      const r = parseNumber(raw);
      if (!r.ok) return { value: undefined, error: `${label} “${cleanText(raw)}” ${r.error}.` };
      let n = r.value;
      if (n < 0) return { value: undefined, error: `${label} cannot be negative.` };
      let warning;
      if (field.type === 'integer' && !Number.isInteger(n)) {
        const rounded = Math.round(n);
        warning = `${label} ${n} rounded to ${rounded}.`;
        n = rounded;
      } else if (field.type === 'number') {
        n = roundTo(n, field.dp ?? 2);
      }
      if (field.max !== undefined && n > field.max) {
        warning = [warning, `${label} ${n} is unusually high — please check it.`].filter(Boolean).join(' ');
      }
      return { value: n, warning };
    }

    case 'date': {
      const r = parseDate(raw, { date1904: ctx.date1904 });
      if (!r.ok) return { value: undefined, error: `${label} “${cleanText(raw)}” ${r.error}.` };
      const warning = ctx.today && r.value > ctx.today ? `${label} ${r.value} is in the future.` : undefined;
      return { value: r.value, warning };
    }

    case 'size': {
      const text = cleanText(raw);
      return { value: canonicalTyreSize(text, field.vocab) || text };
    }

    case 'vocab': {
      const text = cleanText(raw);
      const canon = matchVocab(text, field.vocab);
      if (canon) return { value: canon };
      return { value: text, warning: `${label} “${text}” is not in the standard list; it will be saved as entered.` };
    }

    case 'supplier': {
      // ctx.customerId: the tyre's customer (its yard's) — see suppliersForCustomer.
      const text = cleanText(raw);
      const all = ctx.suppliers || [];
      const own = suppliersForCustomer(all, ctx.customerId);
      const { record: s, ambiguous } = findSupplier(text, own);
      if (s) return { value: s.supplierId };
      if (ambiguous) return { value: undefined, warning: `Supplier “${text}” matches ${ambiguous} suppliers, so no supplier will be recorded — rename one under Setup → Suppliers.` };
      // On file, but for ANOTHER customer: say so — the API would refuse it.
      const other = own === all ? null : findSupplier(text, all.filter((x) => !own.includes(x))).record;
      if (other) {
        const names = ctx.customerNames || new Map();
        const owner = names.get(String(other.customerId)) || 'another customer';
        const mine = names.get(String(ctx.customerId)) || 'this yard’s customer';
        return { value: undefined, warning: `Supplier “${text}” is a supplier of ${owner}, but this tyre’s yard belongs to ${mine} — suppliers are kept per customer, so add it for ${mine} first. No supplier will be recorded.` };
      }
      return { value: undefined, warning: `Supplier “${text}” was not found under Setup → Suppliers, so no supplier will be recorded.` };
    }

    case 'yard': {
      const text = cleanText(raw);
      const { record: y, ambiguous } = findYard(text, ctx.yards || []);
      if (y) return { value: y.Id, yard: y };
      if (ambiguous) return { value: undefined, error: `Yard “${text}” matches ${ambiguous} yards — use the yard as the template lists it (“name · customer”).` };
      return { value: undefined, error: `Yard “${text}” was not found.` };
    }

    default: {
      const text = cleanText(raw);
      return { value: text || undefined };
    }
  }
}

// ---------------------------------------------------------------------------
// Catalogue suggestions on UPDATE
//
// The tyre form auto-fills from the catalogue when ADDING a tyre, but when
// EDITING one it fills only the fields that are still empty — it never replaces a
// value already recorded on the physical tyre. The import template auto-fills
// with formulas, and the reader marks those cells (row.suggested). For a tyre that
// already exists, a suggested value is therefore dropped wherever the existing
// record has a value: the same rule. A TYPED value always updates. Run at Review
// (against the sweep) and again at write time (against GET /tyre/{id}).
// ---------------------------------------------------------------------------

/**
 * Remove suggested fields the existing tyre already has a value for.
 * @returns {string[]} the field keys removed
 */
export function keepExistingOverSuggestions(payload, suggestedKeys = [], existing = {}) {
  const kept = [];
  for (const key of suggestedKeys) {
    const has = existing && existing[key] !== null && existing[key] !== undefined && String(existing[key]).trim() !== '';
    if (has && key in payload) { delete payload[key]; kept.push(key); }
  }
  return kept;
}

/** "Catalogue suggestions left the existing Size and Grade unchanged." */
export function suggestionNote(keys = []) {
  const labels = keys.map((k) => getImportField(k)?.label?.replace(/ \(.*\)$/, '') || k);
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}` : labels[0];
  return `Catalogue suggestions left the existing ${list} unchanged.`;
}

// Cross-field sanity checks. Warnings only: the data may be odd but true.
function crossChecks(p) {
  const w = [];
  const init = p.initialTyreTreadDepth;
  const cur = p.tyreTreadDepth;
  const min = p.minimumTreadForRetread;
  if (init !== undefined && cur !== undefined && cur > init) {
    w.push(`Current tread (${cur} mm) is deeper than the initial tread (${init} mm).`);
  }
  if (init !== undefined && min !== undefined && min >= init) {
    w.push(`Minimum tread for retread (${min} mm) is not below the initial tread (${init} mm).`);
  }
  return w;
}

/**
 * Build import rows.
 *
 * @param {object} args
 * @param {Array<Array<*>>} args.rows          the sheet matrix
 * @param {number} args.headerRow              index of the header row; data starts below it
 * @param {string[]} args.mapping              field key (or '') per column
 * @param {object} args.ctx
 * @param {{Id:*,name:string,customerId?:*}|null} args.ctx.yard  the chosen target yard
 * @param {Array} [args.ctx.yards]             every selectable yard (for a Yard column)
 * @param {Array} [args.ctx.suppliers]         the customer's suppliers
 * @param {Map<string,object>} [args.ctx.existing]  tyreIdKey → existing tyre row
 * @param {'update'|'skip'} [args.ctx.mode]    what to do with existing tyres
 * @param {string} [args.ctx.today]            'YYYY-MM-DD', for the future-date warning
 * @param {boolean} [args.ctx.date1904]
 * @param {object[]|null} [args.ctx.catalogue]  the customer's tyre catalogue (active
 *                                             and inactive); null = unavailable, no checks
 * @param {boolean} [args.ctx.addToCatalogue]  missing specs will be added before import
 * @returns {{ rows: object[], summary: object, catalogue: object }}
 */
export function buildImportRows({ rows = [], headerRow = 0, mapping = [], ctx = {} } = {}) {
  const existing = ctx.existing || new Map();
  const mode = ctx.mode === EXISTING_MODE.SKIP ? EXISTING_MODE.SKIP : EXISTING_MODE.UPDATE;
  const mapped = mapping
    .map((key, col) => ({ col, field: key ? getImportField(key) : null }))
    .filter((m) => m.field);

  const out = [];
  const firstSeen = new Map(); // tyreIdKey → sheet row number
  // customerId → name, from the yards (the only place the screen has them).
  const customerNames = new Map();
  for (const y of [...(ctx.yards || []), ctx.yard].filter(Boolean)) {
    if (hasValue(y.customerId) && y.customerName) customerNames.set(String(y.customerId), String(y.customerName));
  }

  for (let r = headerRow + 1; r < rows.length; r++) {
    const cells = rows[r] || [];
    // A row with nothing in any MAPPED column is padding, not data.
    if (mapped.every(({ col }) => isBlankCell(cells[col]))) continue;

    const rowNumber = r + 1; // as the user sees it in Excel
    const errors = [];
    const warnings = [];
    const payload = {};
    let rowYard = null;

    const suggested = [];
    const deferred = []; // supplier: resolved once the row's yard (its customer) is known
    for (const { col, field } of mapped) {
      if (field.type === 'supplier') { deferred.push({ col, field }); continue; }
      const res = coerceCell(field, cells[col], ctx);
      if (res.error) errors.push(res.error);
      if (res.warning) warnings.push(res.warning);
      if (res.yard) rowYard = res.yard;
      if (res.value !== undefined) {
        payload[field.key] = res.value;
        // A value the import template's catalogue formula filled in, not one typed.
        if (ctx.suggested?.has(`${r}:${col}`)) suggested.push(field.key);
      }
    }

    // Identity.
    const id = payload.externalTyreId;
    const key = tyreIdKey(id);
    let idError = true;
    if (!key) {
      errors.unshift('Tyre ID is blank.');
    } else if (firstSeen.has(key)) {
      errors.unshift(`Tyre ID ${id} appears again — first on row ${firstSeen.get(key)}.`);
    } else {
      idError = false;
      firstSeen.set(key, rowNumber);
    }

    // Placement: a Yard column value wins; otherwise the chosen yard.
    const yard = rowYard || ctx.yard || null;
    if (yard) payload.yardId = yard.Id;
    else if (!errors.some((e) => e.startsWith('Yard'))) errors.push('No yard — choose a yard for this import.');

    // customerId follows the yard's owning customer (a child-customer yard keeps
    // its tyres under that child). When the yard object doesn't say, it is omitted
    // and the API scopes the tyre to the signed-in account.
    if (yard?.customerId !== undefined && yard?.customerId !== null && yard?.customerId !== '') {
      payload.customerId = yard.customerId;
    }

    for (const { col, field } of deferred) {
      const res = coerceCell(field, cells[col], { ...ctx, customerId: yard?.customerId, customerNames });
      if (res.warning) warnings.push(res.warning);
      if (res.value !== undefined) payload[field.key] = res.value;
    }

    warnings.push(...crossChecks(payload));

    // Existing tyre?
    const found = key && !idError ? existing.get(key) : null;
    let action;
    // A row that will be skipped is never written, so its data problems can't do
    // harm — it reads as Skip (problems still listed), not as a blocking Error.
    if (found && mode === EXISTING_MODE.SKIP) action = IMPORT_ACTION.SKIP;
    else if (errors.length) action = IMPORT_ACTION.ERROR;
    else if (found) action = mode === EXISTING_MODE.SKIP ? IMPORT_ACTION.SKIP : IMPORT_ACTION.UPDATE;
    else action = IMPORT_ACTION.CREATE;

    if (action === IMPORT_ACTION.UPDATE) {
      const fromYard = found.yardId ?? null;
      if (fromYard !== null && yard && String(fromYard) !== String(yard.Id)) {
        warnings.push(`Already on file in ${found.yardName || 'another yard'} — updating moves it to ${yard.name || 'this yard'}.`);
      }
      if (payload.tyreKm !== undefined && hasDistanceTracking(found)) {
        delete payload.tyreKm;
        warnings.push('Opening distance ignored — PressureTrack is already tracking this tyre’s distance.');
      }
    }

    const notes = [];
    if (action === IMPORT_ACTION.UPDATE) {
      const kept = keepExistingOverSuggestions(payload, suggested, found);
      if (kept.length) notes.push(suggestionNote(kept));
    }

    out.push({ rowNumber, id: id ?? '', key, payload, errors, warnings, notes, suggested, catalogue: null, action, existing: !!found });
  }

  const catalogue = checkCatalogue(out, ctx);
  return { rows: out, summary: summarise(out), catalogue };
}

// ---------------------------------------------------------------------------
// Tyre catalogue
//
// Only rows that will be WRITTEN (create/update) are checked: an error row isn't
// sent and a skipped row isn't changed, so neither says anything about what the
// catalogue should hold. Matching and planning are the shared rules in
// tyre-catalogue-match.js.
//
//   matched    nothing to say
//   missing    adding  → a note ("an entry will be added"), not a warning: the
//                        importer is about to fix it
//              not     → a warning (with the admin hint when relevant)
//   inactive   a warning: an entry exists but is switched off; adding a second
//              one would only duplicate it
//   incomplete no manufacturer or model — nothing to match on, nothing said
// ---------------------------------------------------------------------------

export const CATALOGUE_STATUS = Object.freeze({
  MATCHED: 'matched', MISSING: 'missing', INACTIVE: 'inactive', INCOMPLETE: 'incomplete'
});

function checkCatalogue(rows, ctx) {
  const entries = Array.isArray(ctx.catalogue) ? ctx.catalogue : null;
  const result = { available: !!entries, plan: [], missingRows: 0, inactiveRows: 0 };
  if (!entries) return result;

  const writable = rows.filter((r) => r.action === IMPORT_ACTION.CREATE || r.action === IMPORT_ACTION.UPDATE);
  for (const r of writable) {
    const spec = catalogueSpecFromTyre(r.payload);
    const { status, entry } = matchCatalogue(spec, entries);
    r.catalogue = { status, label: catalogueSpecLabel(spec) };
    if (status === CATALOGUE_STATUS.MISSING) {
      result.missingRows++;
      if (ctx.addToCatalogue) r.notes.push('Not in the tyre catalogue — a catalogue entry will be added.');
      else {
        r.warnings.push(`Not in the tyre catalogue: ${r.catalogue.label}.${ctx.catalogueHint ? ` ${ctx.catalogueHint}` : ''}`);
      }
    } else if (status === CATALOGUE_STATUS.INACTIVE) {
      result.inactiveRows++;
      r.warnings.push(`Matches an inactive catalogue entry (${catalogueSpecLabel(entry)}). Reactivate it under Setup → Tyre catalogue to offer it as a choice.`);
    }
  }

  result.plan = planCatalogueEntries(
    writable.filter((r) => r.catalogue?.status === CATALOGUE_STATUS.MISSING),
    entries
  );
  return result;
}

export function summarise(rows = []) {
  const s = { total: rows.length, create: 0, update: 0, skip: 0, error: 0, warning: 0, importable: 0, catalogue: 0 };
  for (const r of rows) {
    s[r.action]++;
    if (r.action !== IMPORT_ACTION.ERROR && r.warnings.length) s.warning++;
    if (r.catalogue && (r.catalogue.status === 'missing' || r.catalogue.status === 'inactive')) s.catalogue++;
  }
  s.importable = s.create + s.update;
  return s;
}

/** Rows the runner should process (create/update/skip; never errors). */
export function rowsToRun(rows = []) {
  return rows.filter((r) => r.action !== IMPORT_ACTION.ERROR);
}

/** Fields the mapping dropdown offers, grouped for <optgroup>. */
export function fieldGroups() {
  const groups = new Map();
  for (const f of IMPORT_FIELDS) {
    if (!groups.has(f.group)) groups.set(f.group, []);
    groups.get(f.group).push(f);
  }
  return [...groups.entries()].map(([name, fields]) => ({ name, fields }));
}
